import React, { useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as DownIcon } from "../../assets/icons/down.svg";
import { genericService } from "../../services/generic.service";
import styles from "../../styles/components/TimezoneSelect.module.css";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

// El catálogo de zonas horarias es grande (~480) y estable: lo cargamos una sola
// vez y lo compartimos entre todas las instancias del selector.
let cachedTimezones = null;
let inflightRequest = null;

function loadTimezones() {
  if (cachedTimezones) return Promise.resolve(cachedTimezones);
  if (!inflightRequest) {
    inflightRequest = genericService
      .timezones()
      .then((data) => {
        cachedTimezones = {
          defaultTimezone: data?.default ?? null,
          options: Array.isArray(data?.timezones) ? data.timezones : [],
        };
        return cachedTimezones;
      })
      .finally(() => {
        inflightRequest = null;
      });
  }
  return inflightRequest;
}

// Solo para tests: reinicia la caché a nivel de módulo entre casos.
export function __clearTimezoneCacheForTests() {
  cachedTimezones = null;
  inflightRequest = null;
}

// La opción "system" (usar la zona del sistema) es puramente de UI: se antepone en
// el front cuando se pide (preferencia de usuario), sin pegarle distinto al backend.
const SYSTEM_OPTION = { value: "system", label: "Mismo que el sistema" };

// Resuelve el label legible (con offset) de una zona horaria a partir de su
// valor IANA, usando el mismo catálogo cacheado. Mientras carga (o si falla)
// devuelve el valor crudo como fallback.
export function useTimezoneLabel(value) {
  const [label, setLabel] = useState(() =>
    value === SYSTEM_OPTION.value ? SYSTEM_OPTION.label : value || "",
  );

  useEffect(() => {
    let active = true;
    if (!value) {
      setLabel("");
      return undefined;
    }

    if (value === SYSTEM_OPTION.value) {
      setLabel(SYSTEM_OPTION.label);
      return undefined;
    }
    setLabel(value);
    loadTimezones()
      .then((data) => {
        if (!active) return;
        const match = data.options.find((option) => option.value === value);
        setLabel(match?.label || value);
      })
      .catch(() => {
        if (active) setLabel(value);
      });
    return () => {
      active = false;
    };
  }, [value]);

  return label;
}

const TimezoneSelect = ({
  label = "Zona horaria",
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  includeSystem = false,
  placeholder = "Seleccionar zona horaria...",
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    loadTimezones()
      .then((data) => {
        if (active) setOptions(data.options);
      })
      .catch(() => {
        if (active) setOptions([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // Reusa el catálogo cacheado y antepone "system" solo si se pidió (perfil de usuario).
  const allOptions = useMemo(
    () => (includeSystem ? [SYSTEM_OPTION, ...options] : options),
    [options, includeSystem],
  );

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  const selectedOption = useMemo(
    () => allOptions.find((option) => option.value === value) || null,
    [allOptions, value],
  );

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return allOptions;
    return allOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(normalizedQuery) ||
        option.value.toLowerCase().includes(normalizedQuery),
    );
  }, [allOptions, query]);

  const handleToggle = () => {
    setQuery("");
    setOpen((prev) => !prev);
  };

  const handleSelect = (option) => {
    onChange?.(option.value);
    setOpen(false);
    setQuery("");
  };

  const displayLabel = selectedOption?.label || value || "";

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      {label ? (
        <div className={styles.label}>
          {label}
          {required ? <span className={styles.required}> *</span> : null}
        </div>
      ) : null}

      <button
        type="button"
        className={cx(
          styles.control,
          open && styles.open,
          error && styles.error,
          disabled && styles.disabled,
        )}
        onClick={handleToggle}
        disabled={disabled}
      >
        <span className={displayLabel ? styles.value : styles.placeholder}>
          {displayLabel || placeholder}
        </span>
        <DownIcon className={cx(styles.chevron, open && styles.chevronUp)} />
      </button>

      {open && (
        <div className={styles.menu}>
          <div className={styles.searchRow}>
            <input
              ref={searchInputRef}
              className={styles.search}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar..."
            />
          </div>

          <div className={styles.optionsList}>
            {loading ? (
              <div className={styles.empty}>Cargando...</div>
            ) : filteredOptions.length === 0 ? (
              <div className={styles.empty}>Sin resultados</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={cx(
                    styles.option,
                    option.value === value && styles.optionSelected,
                  )}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {error ? <div className={styles.errorText}>{error}</div> : null}
    </div>
  );
};

export default TimezoneSelect;
